import { Component, ElementRef, HostListener, inject, signal, ViewChild } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationComponent } from '../../components/confirmation/confirmation.component';
import { AuthService, HelpContent, HelpSection } from '../../../core/providers/guards/auth.service';
import { ApiService } from '../../../core/service/api.service';
import { environment } from '../../../../environments/environment';
import { ChangePassword } from '../../../features/change-password/change-password';
import { getOrgTypeIcon } from '../../../core/utils/org-type.utils';
import { API_ENDPOINTS } from '../../../core/api/api-endpoint';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AccessKeyModalComponent } from '../../../features/access-key/access-key-modal/access-key-modal.component';

@Component({
  selector: 'app-layout',
  imports: [TranslatePipe, RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss']
})
export class LayoutComponent {

  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private api = inject(ApiService);
  private router = inject(Router);
  private modalService = inject(NgbModal);
  private subscription?: Subscription;
  private sanitizer = inject(DomSanitizer);

  @ViewChild('supportBody') supportBody?: ElementRef<HTMLElement>;

  isExpanded = false;
  isMobileOpen = false;
  isDesktop = true;
  isHighlighted = false;

  permissions = signal<Record<string, boolean>>({});

  get isCompactSupportSidebar(): boolean {
    const currentUrl = this.router.url.toLowerCase();
    return currentUrl.includes('/users/create') || currentUrl.includes('/ledger/create') || currentUrl.includes('/asset-progress/detail');
  }

  get showFooter(): boolean {
    const currentUrl = this.router.url.toLowerCase();
    return !(currentUrl.includes('/users/create') || currentUrl.includes('/ledger/create'));
  }

  // Help & Support
  supportVideoUrl!: SafeResourceUrl;
  helpPageContext: 'user' | 'create-user' | 'create-role' | 'create-ledger' | 'clone-ledger' | 'asset-progress-detail' = 'user';
  helpContent: HelpContent | null = null;

  // ── Computed: active sections & metadata based on current page context ────
  get supportSections(): HelpSection[] {
    return this.helpContent?.sections ?? [];
  }

  get helpSectionTitle(): string {
    return this.helpContent?.title ?? 'About Onboarding User';
  }

  get helpSectionIntro(): string {
    return this.helpContent?.intro ?? 'The Onboard User screen allows administrators to register new users in FoodTraze. Complete the required information, assign system access, and configure user permissions to enable platform access.';
  }

  get helpReadTime(): string {
    return this.helpContent?.readTime ?? '3 min read';
  }

  // ── User-onboarding help content ──────────────────────────────────────────
  private userSupportSections: HelpSection[] = [
    {
      id: 'complete-user-information',
      title: 'Complete User Information',
      steps: [
        {
          title: 'Personal Information',
          description: [
            { text: 'User ID – Automatically generated by the system.' },
            { text: "Full Name – Enter the user's full name." },
            { text: "Age – Enter the user's age." },
            { text: "Social Status – Enter or select the user's social category or occupation." }
          ]
        },
        {
          title: 'Contact & Language',
          description: [
            { text: 'Mobile Number – Enter a valid contact number.' },
            { text: "Preferred Language – Select the user's preferred language." },
            { text: 'National ID / Identity Number – Enter a government-issued identification number.' }
          ]
        },
        {
          title: 'Address Information',
          description: [
            { text: 'Select the Country.' },
            { text: 'Enter the complete address.' },
            { text: 'Provide any additional location information if applicable.' }
          ]
        }
      ]
    },
    {
      id: 'upload-verification-documents',
      title: 'Upload Verification Documents',
      steps: [
        {
          title: 'Steps',
          description: [
            { text: 'Click the upload area.' },
            { text: 'Select document files from your device.' },
            { text: 'Wait for the upload to complete.' },
            { text: 'Verify that the files are listed successfully.' }
          ]
        },
        {
          title: 'Supported Formats',
          description: [
            { text: 'PDF' },
            { text: 'JPG' },
            { text: 'JPEG' },
            { text: 'PNG' }
          ]
        },
        {
          title: 'Upload Rules',
          description: [
            { text: 'Maximum 3 files can be uploaded.' },
            { text: 'Upload clear and readable documents.' },
            { text: 'Remove and re-upload files if corrections are required.' }
          ]
        }
      ]
    },
    {
      id: 'configure-login-access',
      title: 'Configure Login Access',
      steps: [
        {
          title: 'Username (Email ID)',
          description: [
            { text: 'Enter a valid email address that will be used for login.' }
          ]
        },
        {
          title: 'Password',
          description: [
            { text: 'A temporary password is automatically generated by FoodTraze.' }
          ]
        },
        {
          title: 'Account Status',
          description: [
            { text: 'Enabled – User can log in.' },
            { text: 'Disabled – User account is inactive.' }
          ]
        },
        {
          title: 'Note',
          description: [
            { text: 'The login credentials will be sent to the registered email address.' }
          ]
        }
      ]
    },
    {
      id: 'assign-user-role-branch',
      title: 'Assign User Role & Branch',
      steps: [
        {
          title: 'Overview',
          description: [
            { text: "Define the user's access level and branch assignment within FoodTraze." }
          ]
        },
        {
          title: 'User Type',
          description: [
            { text: 'Select the appropriate user type.' },
            {
              heading: 'User',
              items: [
                'Standard platform user.',
                'Can be assigned to only one branch.',
                'Access is limited to the assigned branch and permitted activities.'
              ]
            },
            {
              heading: 'Super User',
              items: [
                'Advanced user with broader operational access.',
                'Can be assigned to multiple branches.',
                'Can manage and monitor activities across all assigned branches.'
              ]
            }
          ]
        },
        {
          title: 'Role',
          description: [
            { text: "Select the role that determines the user's permissions and responsibilities within the platform." }
          ]
        },
        {
          title: 'Branch Assignment',
          description: [
            { text: 'Search and select the required branch(es).' },
            { text: 'For User, only one branch can be assigned.' },
            { text: 'For Super User, multiple branches can be assigned.' },
            { text: 'Selected branches will appear in the Added Branches section.' }
          ]
        },
        {
          title: 'Submit',
          description: [
            { text: 'After reviewing all details, click Submit to create the user account.' },
            {
              heading: 'Result',
              items: [
                'The user will be onboarded successfully and granted access based on the selected user type, role, and branch assignments.'
              ]
            }
          ]
        }
      ]
    }
  ];

  // ── Create Asset Ledger help content ──────────────────────────────────────
  private createLedgerSupportSections: HelpSection[] = [
    {
      id: 'ledger-information',
      title: 'Ledger Information',
      steps: [
        {
          title: 'Fields',
          description: [
            { text: 'Ledger ID – Automatically generated by the system. Unique identifier. Cannot be edited.' },
            { text: 'Ledger Name – Enter a meaningful name for the asset (e.g. Main Warehouse, Field A, Processing Unit 1).' },
            { text: 'Location / Field – Specify the physical location or field where the asset is situated.' },
            { text: 'Expiry Date – Select the date until which the asset remains valid.' }
          ]
        },
        {
          title: 'Tips',
          description: [
            { text: 'Use descriptive names that are easy to identify.' },
            { text: 'Verify the location before saving.' },
            { text: 'Ensure the expiry date is updated whenever the asset validity changes.' }
          ]
        }
      ]
    },
    {
      id: 'pick-an-asset',
      title: 'Pick an Asset',
      steps: [
        {
          title: 'How to Use',
          description: [
            { text: 'Search using the asset name.' },
            { text: 'Click the required asset card.' },
            { text: 'The selected asset becomes associated with this ledger.' }
          ]
        },
        {
          title: 'Tips',
          description: [
            { text: 'Choose only one asset that matches the ledger.' },
            { text: 'If the required asset is unavailable, contact your administrator to register it first.' }
          ]
        }
      ]
    },
    {
      id: 'add-users',
      title: 'Add Users',
      steps: [
        {
          title: 'How to Use',
          description: [
            { text: 'Search users by name or branch.' },
            { text: 'Select one or more users.' },
            { text: 'Selected users appear in the right panel.' },
            { text: 'Remove users if necessary before saving.' }
          ]
        },
        {
          title: 'Assigned Users Can',
          description: [
            { text: 'Record traceability events.' },
            { text: 'Capture KDE information.' },
            { text: 'Review asset activities.' },
            { text: 'Participate in assigned CTEs.' }
          ]
        },
        {
          title: 'Tips',
          description: [
            { text: 'Assign only authorized personnel.' },
            { text: 'Multiple users can be added.' },
            { text: 'Users can be modified later if permissions change.' }
          ]
        }
      ]
    },
    {
      id: 'assign-ctes-users',
      title: 'Assign CTEs & Users',
      steps: [
        {
          title: 'How to Use',
          description: [
            { text: 'Select CTEs from the Available CTEs list.' },
            { text: 'Move them to Selected CTEs.' },
            { text: 'Configure each CTE.' },
            { text: 'Assign one or more users responsible for recording that event.' },
            { text: 'Save the configuration.' }
          ]
        },
        {
          title: 'Configure CTE',
          description: [
            { text: 'Applicable KDEs.' },
            { text: 'Responsible user(s).' },
            { text: 'Event sequence.' },
            { text: 'Validation rules (if configured).' }
          ]
        },
        {
          title: 'Tips',
          description: [
            { text: 'Assign users for every CTE.' },
            { text: 'Arrange CTEs in the correct operational sequence.' },
            { text: 'Verify KDE configuration before creating the ledger.' }
          ]
        }
      ]
    }
  ];

  // ── Clone Asset Ledger help content ───────────────────────────────────────
  private cloneLedgerSupportSections: HelpSection[] = [
    {
      id: 'clone-ledger-information',
      title: 'Ledger Information',
      steps: [
        {
          title: 'Auto-filled (Editable)',
          description: [
            { text: "Ledger Name – Automatically appended with the current user's name. You may rename it before saving." },
            { text: 'Location / Field – Copied from the original.' },
            { text: 'Expiry Date – Copied from the original.' },
            { text: 'Selected Asset, Assigned Users, Assigned CTEs, and CTE configuration are all copied.' }
          ]
        },
        {
          title: 'Auto-generated',
          description: [
            { text: 'Ledger ID is generated automatically for the new ledger and cannot be edited.' }
          ]
        },
        {
          title: 'Important',
          description: [
            { text: 'Review all copied information to ensure it is applicable to the new asset.' },
            { text: 'Save the cloned ledger only after verifying all details.' }
          ]
        }
      ]
    },
    {
      id: 'clone-pick-asset',
      title: 'Pick an Asset',
      steps: [
        {
          title: 'Auto-filled',
          description: [
            { text: 'The asset selected in the original ledger is copied automatically.' }
          ]
        },
        {
          title: 'You Can',
          description: [
            { text: 'Keep the same asset.' },
            { text: 'Select a different asset if required.' }
          ]
        },
        {
          title: 'Tips',
          description: [
            { text: 'Use cloning only when the new ledger has similar traceability requirements.' },
            { text: 'Verify that the selected asset matches the operational purpose of the cloned ledger.' }
          ]
        }
      ]
    },
    {
      id: 'clone-add-users',
      title: 'Add Users',
      steps: [
        {
          title: 'Auto-filled',
          description: [
            { text: 'All users assigned to the original ledger are automatically copied.' }
          ]
        },
        {
          title: 'You Can',
          description: [
            { text: 'Add additional users.' },
            { text: 'Remove users.' },
            { text: 'Change responsibilities before saving.' }
          ]
        },
        {
          title: 'Tips',
          description: [
            { text: 'Ensure only authorized users have access.' },
            { text: 'Verify user assignments before creating the cloned ledger.' }
          ]
        }
      ]
    },
    {
      id: 'clone-assign-ctes',
      title: 'Assign CTEs & Users',
      steps: [
        {
          title: 'Auto-filled',
          description: [
            { text: 'Selected CTEs, CTE order, KDE configuration, assigned users, and workflow settings are all copied.' }
          ]
        },
        {
          title: 'You Can',
          description: [
            { text: 'Add or remove CTEs.' },
            { text: 'Modify KDE configuration.' },
            { text: 'Change assigned users.' },
            { text: 'Reorder CTEs if the operational workflow differs.' }
          ]
        },
        {
          title: 'Tips',
          description: [
            { text: 'Review every CTE after cloning.' },
            { text: "Ensure the workflow reflects the new asset's operations." },
            { text: 'Confirm that responsible users are correctly assigned for audit readiness.' }
          ]
        }
      ]
    }
  ];

  languages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'ta', name: 'தமிழ்' },
    { code: 'zh', name: '中文' },
    { code: 'es', name: 'Español' },
    { code: 'ar', name: 'العربية' },
    { code: 'fr', name: 'Français' },
    { code: 'pt', name: 'Português' },
    { code: 'id', name: 'Bahasa Indonesia' },
    { code: 'de', name: 'Deutsch' },
    { code: 'ja', name: '日本語' },
  ];

  selectedLang = 'en';
  selectedLangName = 'English';
  aliasOrg: string = '';
  appVersion: string = environment.appVersion;
  userName: string = '';
  userInitials: string = '';

  menus: any = [
    {
      permission: 'dashboard',
      route: '/dashboard',
      icon: 'assets/img/layout/dashboard.svg',
      label: 'LAYOUT_DASHBOARD'
    },
    {
      permission: 'asset-ledger',
      route: '/ledger',
      icon: 'assets/img/layout/asset-ledger.svg',
      label: 'ASSET_LEDGER'
    },
    {
      permission: 'asset-progress',
      route: '/asset-progress',
      icon: 'assets/img/layout/event.svg',
      label: 'ASSET_PROGRESS.TITLE'
    },
    {
      permission: 'user-management',
      route: '/users',
      icon: 'assets/img/layout/user.svg',
      label: 'LAYOUT.USER_NETWORK'
    },
    {
      key: 'asset-transfer',
      label: 'LAYOUT.ASSET_TRANSFERS',
      icon: 'assets/img/layout/asset-transfer.svg',
      active: () => this.isCurrentActive('asset-transfer'),
      children: [
        {
          route: '/asset-transfer/send-asset',
          icon: 'assets/img/layout/asset-transfer.svg',
          label: 'SEND_ASSET.TITLE'
        },
        {
          route: '/asset-transfer/received-assets',
          icon: 'assets/img/layout/incoming-transfer.svg',
          label: 'INCOMING_TRANSFERS.TITLE'
        },
        {
          route: '/asset-transfer/sent-assets-history',
          icon: 'assets/img/layout/transfer-history.svg',
          label: 'ASSET_TRANSFER_HISTORY.TITLE'
        }
      ]
    },
    {
      key: 'Trust QR',
      label: 'LAYOUT.TRUST_QR',
      icon: 'assets/img/layout/trust-qr.svg',
      active: () => this.isCurrentActive('qr-traceability'),
      children: [
        {
          permission: 'qr-traceability',
          route: '/qr-traceability/list',
          icon: 'assets/img/layout/qr-code-traze.svg',
          label: 'LAYOUT.TRACEABILITY_QR'
        },
        {
          route: '/qr-traceability/dpp-qr',
          icon: 'assets/img/layout/dpp-qr.svg',
          label: 'DPP QR'
        },
      ]
    },
    {
      route: '/compliance',
      icon: 'assets/img/layout/compliance.svg',
      label: 'LAYOUT.COMPLIANCE'
    },
    {
      route: '/report-center',
      icon: 'assets/img/layout/report-center.svg',
      label: 'LAYOUT.REPORT_CENTER',
      resetKey: 'report-center-state'
    },
    {
      key: 'settings',
      label: 'LAYOUT.SETTINGS',
      icon: 'assets/img/layout/settings.svg',
      active: () => this.isCurrentActive('settings'),
      children: [
        {
          route: '/roles-permissions',
          icon: 'assets/img/layout/permission.svg',
          label: 'ROLES.TITLE'
        },
        {
          route: '/branch-master',
          icon: 'assets/img/layout/branch.svg',
          label: 'BRANCH.TITLE'
        },
        {
          key: 'access-key',
          label: 'ACCESS_KEY.API_KEY',
          icon: 'assets/img/layout/settings.svg',
          modal: true,
        }
        
        
      ]
    }
  ];

  menuOpen: Record<string, boolean> = {
    user: false,
    settings: false
  };

  toggleMenu(key: string) {
    const isCurrentlyOpen = this.menuOpen[key];
    Object.keys(this.menuOpen).forEach(k => {
      this.menuOpen[k] = false;
    });
    this.menuOpen[key] = !isCurrentlyOpen;
  }

  isOpen(key: string) {
    return this.menuOpen[key];
  }

  get orgTypeIcon(): string {
    return getOrgTypeIcon(this.aliasOrg);
  }

  get userProfileImage(): string | null {
    const { imageName, imagePath } = this.authService.userData?.userDetails ?? {};
    if (imageName && imagePath) {
      return this.api.getImageUrl(imageName, imagePath, 36, 36);
    }
    return null;
  }

  private resetSupportScroll(): void {
    requestAnimationFrame(() => {
      this.supportBody?.nativeElement.scrollTo({ top: 0, behavior: 'auto' });
    });
  }

  ngOnInit(): void {
    this.subscription = this.authService.helpModalTrigger$.subscribe(res => {
      this.isHighlighted = res;
      if (res) {
        this.resetSupportScroll();
        if (!this.supportVideoUrl) {
          this.supportVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            'https://www.youtube.com/embed/eIdgeEHEGmY'
          );
        }
      }
    });

    this.authService.helpPageContext$.subscribe(page => {
      this.helpPageContext = page;
      if (this.isHighlighted) {
        this.resetSupportScroll();
      }
    });

    this.authService.helpContent$.subscribe(content => {
      this.helpContent = content;
      if (this.isHighlighted) {
        this.resetSupportScroll();
      }
    });

    const savedLang = sessionStorage.getItem('lang') || 'en';
    this.selectedLang = savedLang;
    const found = this.languages.find(l => l.code === savedLang);
    this.selectedLangName = found ? found.name : 'English';
    const activeLang = this.translate.currentLang || savedLang;
    if (activeLang !== savedLang) {
      this.selectedLang = activeLang;
      const active = this.languages.find(l => l.code === activeLang);
      this.selectedLangName = active ? active.name : 'English';
    }
    this.aliasOrg = this.authService.aliasOrg;
    this.permissions.set(this.authService.permissions);
    const userData = this.authService.userData;
    const rawUserName: string = userData?.userName || '';
    this.userName = rawUserName.includes('@') ? rawUserName.split('@')[0] : rawUserName;
    this.userInitials = this.userName.split(/[\s._-]+/)?.filter(Boolean).slice(0, 2)?.map((w: string) => w[0]?.toUpperCase())?.join('');
    const setupComplete =
      userData?.isTraceabilitySetup == 1 &&
      userData?.isTermsCondition == 1 
    if (userData && !setupComplete && userData.uuid) {
      this.router.navigate(['/profile-setup', userData.uuid]);
    }

    this.loadPermissions();
  }

  private loadPermissions(): void {
    if (this.authService.isAdmin) return;
    this.api.get(API_ENDPOINTS.AUTH_PERMISSION.ME).subscribe({
      next: (res: any) => {
        const perms: Record<string, boolean> = res?.data?.permissions ?? res?.permissions ?? {};
        sessionStorage.setItem('permissions', JSON.stringify(perms));
        this.permissions.set(perms);
      },
      error: () => {
        // On failure keep the cached sessionStorage permissions — no flicker
      }
    });
  }

  hasPermission(key: string): boolean {
    if (this.authService.isAdmin) return true;
    return this.permissions()[key] === true;
  }

  isCurrentActive(type: string): boolean {
    const url = this.router.url;
    switch (type) {
      case 'user':
        return url.includes('/users') || url.includes('/user-groups') || url.includes('/my-profile');
      case 'settings':
        return url.includes('/branch-master') || url.includes('/roles-permissions');
      case 'asset-transfer':
        return url.includes('/transfer-list') || url.includes('/incoming-transfers') || url.includes('/transfer-history');
      case 'qr-traceability':
        return url.includes('qr-traceability');
      default:
        return false;
    }
  }

  changeLanguage(lang: any) {
    this.selectedLang = lang.code;
    this.selectedLangName = lang.name;
    this.translate.use(lang.code);
    sessionStorage.setItem('lang', lang.code);
  }

  @HostListener('window:resize')
  checkScreen() {
    this.isDesktop = window.innerWidth > 1024;
  }

  onDesktopEnter() {
    if (this.isDesktop) this.isExpanded = true;
  }

  onDesktopLeave() {
    if (this.isDesktop) {
      this.isExpanded = false;
    }
  }

  toggleMobile() {
    this.isMobileOpen = !this.isMobileOpen;
  }

  closeMobile() {
    this.isMobileOpen = false;
  }

  onMenuClick(menu: any) {
    this.closeMobile();
    if (menu?.resetKey) {
      sessionStorage.removeItem(menu.resetKey);
    }
    if (menu?.route) {
      this.router.navigate([menu.route]);
    }
  }

  openPasswordModal() {
    const ref = this.modalService.open(ChangePassword, {
      size: 'md', backdrop: 'static',
      backdropClass: 'createcr', modalDialogClass: 'modal-dialog-centered',
    });
  }

         openAccessKeyModal() {
    this.modalService.open(AccessKeyModalComponent, {
      size: 'md', backdrop: 'static',
      backdropClass: 'createcr', modalDialogClass: 'modal-dialog-centered',
    });
  }
     

  

  logout() {
    const modalRef = this.modalService.open(ConfirmationComponent, { centered: true });
    modalRef.componentInstance.title = 'LOGOUT';
    modalRef.componentInstance.message = 'LOGOUT_CONFIRM_MESSAGE';
    modalRef.result.then(
      (confirmed) => {
        if (confirmed) {
          this.authService.logout();
          this.router.navigate(['/auth/login']);
        }
      },
      () => { } // dismissed
    );
  }

  closeModal(): void {
    this.authService.clearHelpHighlight();
  }

}
